import { ITEMS, type ItemId } from '@/data/items';
import { RODS, isRodId } from '@/data/tackle';
import { WEAPONS } from '@/data/weapons';

/**
 * Tools Katie carries, separate from the bag.
 *
 * Tools are not cargo. They do not stack, they are never thrown away, and
 * competing with seeds for hotbar space made the bag feel full before the
 * player had picked anything up. Keeping them on a belt also makes upgrades
 * behave the way players expect: a steel rod does not sit *next to* the bamboo
 * rod, it replaces it.
 */

export type ToolFamily = 'hoe' | 'wateringCan' | 'rod' | 'sword';

export interface ToolDefinition {
  readonly id: ItemId;
  readonly family: ToolFamily;
  /** Higher supersedes lower within the same family. */
  readonly tier: number;
  readonly name: string;
}

export const TOOLS: readonly ToolDefinition[] = [
  { id: 'hoe', family: 'hoe', tier: 1, name: ITEMS.hoe.name },
  { id: 'wateringCan', family: 'wateringCan', tier: 1, name: ITEMS.wateringCan.name },
  ...Object.values(RODS).map((rod) => ({
    id: rod.id,
    family: 'rod' as const,
    tier: rod.tier,
    name: rod.name,
  })),
  // Weapons are a tool family like any other, so the belt, the tool button and
  // the in-hand sprite need no special case for them.
  ...Object.values(WEAPONS).map((weapon) => ({
    id: weapon.id,
    family: 'sword' as const,
    tier: weapon.tier,
    name: weapon.name,
  })),
];

export function toolFor(id: ItemId): ToolDefinition | undefined {
  return TOOLS.find((tool) => tool.id === id);
}

export function isTool(id: ItemId): boolean {
  return toolFor(id) !== undefined;
}

export class ToolBelt {
  /**
   * Tools owned per family, all at the family's best tier.
   *
   * A list rather than one entry, because two tools can be equally good and
   * differently shaped. The Rootcleaver and the Tidewhisper are both tier
   * three: one is slow and enormous, the other long and quick, and neither is
   * an upgrade over the other. With a single slot per family, forging one made
   * the other permanently unobtainable — `acquire` refuses anything not
   * strictly better, so the second blade was silently rejected and the player
   * kept swinging the first.
   *
   * Retirement is therefore for tools that are *beaten*, not merely matched.
   */
  private readonly owned = new Map<ToolFamily, ToolDefinition[]>();
  private active: ItemId | null = null;

  static starting(): ToolBelt {
    const belt = new ToolBelt();
    belt.acquire('hoe');
    belt.acquire('wateringCan');
    belt.acquire('bambooRod');
    belt.acquire('pruningBlade');
    belt.setActive('hoe');
    return belt;
  }

  /**
   * Take ownership of a tool.
   *
   * Returns the tool it replaced, if any, so the UI can say "your bamboo rod
   * has been retired" rather than leaving the player wondering where it went.
   * A lesser tool is ignored outright: picking one up must never downgrade you.
   */
  acquire(id: ItemId): { replaced: ToolDefinition | null } | null {
    const tool = toolFor(id);
    if (!tool) return null;

    const carried = this.owned.get(tool.family) ?? [];
    const best = carried[0];

    // Already have it, or already have better.
    if (best && best.tier > tool.tier) return null;
    if (carried.some((owned) => owned.id === tool.id)) return null;

    if (best && best.tier === tool.tier) {
      // A sidegrade. Kept alongside, and never made active behind the
      // player's back — they chose what they are holding.
      carried.push(tool);
      this.owned.set(tool.family, carried);
      return { replaced: null };
    }

    this.owned.set(tool.family, [tool]);
    // Move the player onto the upgrade if they were holding something it
    // retired; being silently left on a retired tool is the worst outcome.
    if (this.active === null || carried.some((owned) => owned.id === this.active)) {
      this.active = tool.id;
    }

    return { replaced: best ?? null };
  }

  /** True when acquiring this would change nothing — already have it or better. */
  acquireWouldDoNothing(id: ItemId): boolean {
    const tool = toolFor(id);
    if (!tool) return true;
    const carried = this.owned.get(tool.family) ?? [];
    if (carried.some((owned) => owned.id === tool.id)) return true;
    const best = carried[0];
    return best !== undefined && best.tier > tool.tier;
  }

  has(family: ToolFamily): boolean {
    return this.owned.has(family);
  }

  best(family: ToolFamily): ToolDefinition | undefined {
    return this.owned.get(family)?.[0];
  }

  /** Everything currently carried, in a stable order. */
  all(): ToolDefinition[] {
    return TOOLS.filter((tool) =>
      (this.owned.get(tool.family) ?? []).some((owned) => owned.id === tool.id),
    );
  }

  get activeId(): ItemId | null {
    return this.active;
  }

  get activeTool(): ToolDefinition | undefined {
    return this.active ? toolFor(this.active) : undefined;
  }

  setActive(id: ItemId | null): boolean {
    if (id === null) {
      this.active = null;
      return true;
    }
    const tool = toolFor(id);
    if (!tool || !(this.owned.get(tool.family) ?? []).some((owned) => owned.id === id)) {
      return false;
    }
    this.active = id;
    return true;
  }

  /**
   * Give a tool up.
   *
   * Only synthesis and evolution do this, and both consume a weapon on
   * purpose. Nothing else should ever take a tool away — losing a hoe to a
   * bug is the kind of thing that ends a save.
   */
  discard(id: ItemId): boolean {
    const tool = toolFor(id);
    if (!tool) return false;

    const carried = this.owned.get(tool.family) ?? [];
    const kept = carried.filter((owned) => owned.id !== id);
    if (kept.length === carried.length) return false;

    if (kept.length > 0) this.owned.set(tool.family, kept);
    else this.owned.delete(tool.family);

    // Never leave the player holding something they no longer have.
    if (this.active === id) this.active = this.all()[0]?.id ?? null;
    return true;
  }

  /** Step to the next carried tool, for a keyboard shortcut. */
  cycle(): void {
    const carried = this.all();
    if (carried.length === 0) return;
    const index = carried.findIndex((tool) => tool.id === this.active);
    this.active = carried[(index + 1) % carried.length]!.id;
  }

  toSave(): { tools: string[]; active: string | null } {
    return { tools: this.all().map((tool) => tool.id), active: this.active };
  }

  static fromSave(value: unknown): ToolBelt {
    const belt = new ToolBelt();
    const saved = value as { tools?: unknown; active?: unknown } | null;

    if (Array.isArray(saved?.tools)) {
      for (const id of saved.tools) {
        if (typeof id === 'string') belt.acquire(id as ItemId);
      }
    }

    // Never leave a save without the basics: a player who somehow lost their
    // hoe has no way to get another one yet.
    belt.acquire('hoe');
    belt.acquire('wateringCan');
    if (!belt.has('rod')) belt.acquire('bambooRod');
    if (!belt.has('sword')) belt.acquire('pruningBlade');

    if (typeof saved?.active === 'string') {
      const wanted = saved.active as ItemId;
      if (belt.setActive(wanted)) return belt;

      // The saved tool was superseded. Hand over its replacement rather than
      // whatever happens to be first — the player reached for a rod, so give
      // them the rod they now own.
      const family = toolFor(wanted)?.family;
      const successor = family ? belt.best(family) : undefined;
      if (successor && belt.setActive(successor.id)) return belt;
    }

    belt.setActive(belt.all()[0]?.id ?? null);
    return belt;
  }
}

/** True when this item is a rod, which the fishing code needs to know. */
export function isRod(id: ItemId): boolean {
  return isRodId(id);
}
